import React, { useState } from 'react';
import { PricingSection } from './PricingSection';
import { startCheckout } from '../../services/billing';
import { MODE_LABELS, type BillingInterval, type GameMode } from '../../config/plans';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** If the modal was triggered by trying to open a locked mode, name it for context. */
  lockedMode?: GameMode | null;
};

/**
 * Upgrade paywall modal. Pitches Pro and kicks off Stripe Checkout. Shown when a
 * free user tries to access a Pro feature, or from the billing settings CTA.
 */
export const UpgradeModal: React.FC<Props> = ({ isOpen, onClose, lockedMode }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSelect = async (interval: BillingInterval) => {
    setBusy(true);
    setError(null);
    try {
      await startCheckout(interval); // redirects away on success
    } catch (err) {
      setBusy(false);
      setError(err instanceof Error ? err.message : 'Could not start checkout.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Upgrade to Pro"
    >
      <div
        className="relative w-full max-w-md bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto animate-page-in"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700 transition-colors"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="p-6">
          <div className="text-center mb-5">
            <div className="text-3xl mb-1">🎰</div>
            <h2 className="text-2xl font-bold text-white">Unlock Pro</h2>
            <p className="text-sm text-gray-400 mt-1">
              {lockedMode
                ? `${MODE_LABELS[lockedMode]} is a Pro feature. Upgrade to unlock it and everything below.`
                : 'Go beyond the basics with the full training suite.'}
            </p>
          </div>

          <PricingSection compact busy={busy} onSelect={handleSelect} />

          {error && (
            <div className="mt-3 text-sm text-red-400 text-center">{error}</div>
          )}
        </div>
      </div>
    </div>
  );
};
